import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, ShieldCheckIcon, TargetIcon, UserIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function AboutPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-311">
      <SEOHead title="About Viridia Analytics | Phoenix Marketing Agency | Kevin Rogers, Founder" description="Meet Kevin Rogers, founder of Viridia Analytics. A Phoenix-based, owner-operated marketing agency with insider expertise from Yelp and top ad platforms, built for local service businesses." canonical="/about" keywords="about Viridia Analytics, Kevin Rogers, Phoenix marketing agency, Yelp insider, local business marketing, owner-operated agency" jsonLd={{
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Kevin Rogers',
      jobTitle: 'Founder & CEO',
      worksFor: {
        '@type': 'Organization',
        name: 'Viridia Analytics',
        url: 'https://viridiaanalytics.com'
      },
      description: 'Founder of Viridia Analytics. Former Yelp and Zillow insider turned Phoenix marketing agency owner, specializing in AI-powered lead conversion and digital advertising for local service businesses.',
      sameAs: ['https://www.linkedin.com/in/kevinrogers-viridia/'],
      knowsAbout: ['Yelp advertising', 'AI lead conversion', 'digital marketing', 'home services marketing', 'local SEO']
    }} data-id="element-312" />
      {/* HERO SECTION */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center overflow-hidden" data-id="element-313">
        {/* Subtle Grid Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" data-id="element-314"></div>

        <ScrollReveal className="relative z-10" data-id="element-315">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight" data-id="element-316">
            Built by an operator,{' '}
            <span className="text-viridia italic" data-id="element-317">for operators.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-318">
            Viridia Analytics exists because local service businesses deserve
            better than generic agency promises.
          </p>
        </ScrollReveal>
      </section>

      {/* FOUNDER / LEADERSHIP SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-319">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-320">
          <ScrollReveal data-id="element-321">
            <div className="mb-16" data-id="element-322">
              <span className="text-viridia font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-323">
                OUR LEADERSHIP
              </span>
              <h2 className="text-4xl md:text-5xl font-bold" data-id="element-324">
                Driving Growth Through Data
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-325">
            <ScrollReveal direction="right" className="lg:col-span-4" data-id="element-326">
              <div className="aspect-square rounded-2xl border-4 border-viridia/20 shadow-2xl relative overflow-hidden group" data-id="element-327">
                <img src="https://cdn.magicpatterns.com/uploads/q3DhQsm3VboBpsETP7urnD/Kevindramtichadshot.png" alt="Kevin Rogers - Founder & CEO, Viridia Analytics" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-328" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" data-id="element-329"></div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="left" delay={0.2} className="lg:col-span-8" data-id="element-330">
              <div className="prose prose-lg prose-gray max-w-none" data-id="element-331">
                <h3 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-332">
                  Kevin Rogers
                </h3>
                <p className="text-viridia font-semibold text-lg mb-8" data-id="element-333">
                  Founder & CEO, Viridia Analytics
                </p>

                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-334">
                  After years working inside the advertising industry -
                  including time at platforms like Yelp - I saw firsthand how
                  local businesses were being underserved. They were spending
                  money on ads but had no system to capture, follow up, or
                  convert those leads.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-335">
                  Response time is everything in local services. When a
                  homeowner needs a plumber or a roofer, they hire the first
                  person who answers. I watched businesses waste thousands of
                  dollars on ad clicks, only to lose the job because they were
                  busy on a job site and couldn't answer the phone or reply to a
                  Yelp message fast enough.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-336">
                  That's why I started Viridia Analytics. We're not a big agency
                  trying to upsell you on services you don't need. We're a
                  Phoenix-based, owner-operated team that builds real systems -
                  ads, AI lead conversion, and CRM workflows - designed
                  specifically to fix the follow-up problem. We focus on
                  systems, not just ads, because generating a lead doesn't
                  matter if you don't close it.
                </p>
                <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-100 border-l-4 border-l-viridia" data-id="element-337">
                  <p className="text-gray-900 font-medium italic m-0" data-id="element-338">
                    "Every client gets my direct attention. No account managers,
                    no runaround."
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* VALUES SECTION */}
      <section className="py-24 bg-viridia-black" data-id="element-339">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-340">
          <ScrollReveal data-id="element-341">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-16 text-center" data-id="element-342">
              Why Viridia?
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-343">
            <ScrollReveal delay={0.1} data-id="element-344">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 h-full" data-id="element-345">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6" data-id="element-346">
                  <MapPinIcon className="w-7 h-7 text-viridia" data-id="element-347" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-348">
                  Phoenix Local
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-349">
                  We're based in Phoenix and specialize in the local market. We
                  understand the seasonality, competition, and customer behavior
                  unique to Arizona service businesses.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-350">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 h-full" data-id="element-351">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6" data-id="element-352">
                  <ShieldCheckIcon className="w-7 h-7 text-viridia" data-id="element-353" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-354">
                  Platform Insiders
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-355">
                  Our team includes former employees from major ad platforms. We
                  don't guess - we know exactly how the algorithms work and how
                  to maximize your spend.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-356">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 h-full" data-id="element-357">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6" data-id="element-358">
                  <TargetIcon className="w-7 h-7 text-viridia" data-id="element-359" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-360">
                  No Wasted Leads
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-361">
                  Most agencies stop at getting you clicks. We build the entire
                  system - from ad to answer to booked job - so no lead falls
                  through the cracks.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4} data-id="element-362">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 h-full" data-id="element-363">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6" data-id="element-364">
                  <UserIcon className="w-7 h-7 text-viridia" data-id="element-365" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-366">
                  Owner-Operated
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-367">
                  You work directly with the founder. No layers of account
                  managers. Fast decisions, real accountability, and someone who
                  actually cares about your results.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHY CLIENTS TRUST US */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-368">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-369">
          <ScrollReveal data-id="element-370">
            <div className="text-center max-w-3xl mx-auto" data-id="element-371">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-372">
                Why Clients Trust Us
              </h2>
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 md:p-10" data-id="element-373">
                <p className="text-xl text-gray-300 leading-relaxed" data-id="element-374">
                  Our work is documented. See how{' '}
                  <Link to="/case-studies/tfp-landscaping" className="text-blue-400 font-semibold hover:text-blue-300 transition-colors" data-id="element-375">
                    TFP Landscaping increased leads by 284%
                  </Link>{' '}
                  or how{' '}
                  <Link to="/case-studies/finish-coat-painting" className="text-orange-500 font-semibold hover:text-orange-400 transition-colors" data-id="element-376">
                    Finish Coat Painting closed $15.05k from AI bookings
                  </Link>
                  .
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* MISSION SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-377">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-378">
          <ScrollReveal data-id="element-379">
            <TargetIcon className="w-12 h-12 text-viridia mx-auto mb-8 opacity-50" data-id="element-380" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8" data-id="element-381">
              "We built Viridia to solve one problem: local businesses spend
              money on ads, then lose the leads. Our job is to make sure that
              stops happening."
            </h2>
            <p className="text-lg font-semibold text-gray-500 uppercase tracking-widest" data-id="element-382">
              - Kevin Rogers, Founder
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia to-viridia-dark relative overflow-hidden" data-id="element-383">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-384"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-385">
          <ScrollReveal data-id="element-386">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-387">
              Let's Build Something Together
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-388">
              Whether you need ads, AI lead conversion, or a complete growth
              system - let's talk.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-white text-viridia-dark hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-389">
              Book a Call With Kevin <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-390" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}